import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import {
  initializeUserManagement,
  addUser,
  createPrincipalUser,
  addRole,
  addRoleResourcePermission,
  fetchPermissionsByRoleResourceId,
  updateUser,
} from "../../features/user/userSlice";
import { fetchPrincipalList } from "../../features/principal/PrincipalSlice";
import { UserManagementHeader } from "./UserManagementHeader";
import { UsersTab } from "./UsersTab";
import { RolesTab } from "./RolesTab";
import { CreatePrincipalUserModal } from "./modals/CreatePrincipalUserModal";
import { AddUserModal } from "./modals/AddUserModal";
import { AddRoleModal } from "./modals/AddRoleModal";
import { UpdateRoleModal } from "./modals/UpdateRoleModal";

const UserManagementDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"users" | "roles">("users");
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const {
    users,
    roles,
    allotableResourcePermissions,
    permissions,
    loading,
    error,
  } = useAppSelector((state) => state.user);
  const { principalList } = useAppSelector((state) => state.principal);

  // States for Add User Modal
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRoleId, setNewUserRoleId] = useState<number>(
    roles && roles.length > 0 ? roles[0].roleResourceId : 0
  );

  // States for Add Role Modal
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");

  // States for Update Role Modal
  const [showUpdateRole, setShowUpdateRole] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRoleResourceId, setSelectedRoleResourceId] = useState<number | null>(null);

  // States for Create Principal User Modal
  const [showCreatePrincipalUser, setShowCreatePrincipalUser] = useState(false);
  const [principalUserPrincipalId, setPrincipalUserPrincipalId] = useState("");
  const [principalUserName, setPrincipalUserName] = useState("");
  const [principalUserEmail, setPrincipalUserEmail] = useState("");
  const [principalUserPassword, setPrincipalUserPassword] = useState("");

  useEffect(() => {
    dispatch(initializeUserManagement());
  }, [dispatch]);

  useEffect(() => {
    if (showCreatePrincipalUser && !principalList?.length) {
      dispatch(fetchPrincipalList());
    }
  }, [showCreatePrincipalUser, principalList?.length, dispatch]);

  useEffect(() => {
    if (roles.length > 0) {
      if (selectedRoleId === null) {
        setSelectedRoleId(roles[0].roleResourceId);
      }
      if (!newUserRoleId) {
        setNewUserRoleId(roles[0].roleResourceId);
      }
    }
  }, [roles, selectedRoleId, newUserRoleId]);

  const uniqueResources = Array.from(
    new Map(allotableResourcePermissions.map((item) => [item.resourceId, item])).values()
  );

  const handleAddUserSubmit = async () => {
    await dispatch(
      addUser({
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        roleId: newUserRoleId,
        subRole: "0",
        createdAt: new Date(),
        updatedAt: new Date(),
        resourceId: 0,
        role: "",
        roleResourceId: 0,
      })
    );
    setShowAddUser(false);
    setNewUserName("");
    setNewUserEmail("");
    setNewUserPassword("");
    dispatch(initializeUserManagement());
  };

  const handleAddRoleSubmit = async () => {
    await dispatch(
      addRole({
        role: newRoleName,
        description: newRoleDescription,
        resourceId: 0,
        roleResourceId: 0,
        parentId: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      })
    );
    setShowAddRole(false);
    setNewRoleName("");
    setNewRoleDescription("");
    dispatch(initializeUserManagement());
  };

  const handlePermissionToggle = async (permissionType: "read" | "write", resourceId: number) => {
    const role = roles.find((r) => r.roleResourceId === selectedRoleId);
    if (!role) return;
    const resourcePermission = allotableResourcePermissions.find(
      (item) => item.resourceId === resourceId && item.actionName === permissionType
    );
    if (!resourcePermission) return;

    await dispatch(
      addRoleResourcePermission({
        roleResourceId: role.roleResourceId,
        resourceIds: [resourceId],
        permissionIds: resourcePermission.permissionIds,
      })
    );
    dispatch(fetchPermissionsByRoleResourceId(role.roleResourceId));
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setSelectedRoleId(id);
    dispatch(fetchPermissionsByRoleResourceId(id));
  };

  const openUpdateRoleModal = (userId: number, roleResourceId: number) => {
    setSelectedUserId(userId);
    setSelectedRoleResourceId(roleResourceId);
    setShowUpdateRole(true);
  };

  const handleUpdateRoleSubmit = async () => {
    if (selectedUserId !== null && selectedRoleResourceId !== null) {
      await dispatch(updateUser({ userId: selectedUserId, roleResourceId: selectedRoleResourceId }));
      setShowUpdateRole(false);
      dispatch(initializeUserManagement());
    }
  };

  const openCreatePrincipalUser = () => {
    setPrincipalUserPrincipalId("");
    setPrincipalUserName("");
    setPrincipalUserEmail("");
    setPrincipalUserPassword("");
    setShowCreatePrincipalUser(true);
  };

  const handleCreatePrincipalUserSubmit = async () => {
    if (
      !principalUserPrincipalId ||
      !principalUserName.trim() ||
      !principalUserEmail.trim() ||
      !principalUserPassword
    )
      return;
    const result = await dispatch(
      createPrincipalUser({
        principalId: principalUserPrincipalId,
        name: principalUserName.trim(),
        email: principalUserEmail.trim(),
        password: principalUserPassword,
      })
    );
    if (createPrincipalUser.fulfilled.match(result)) {
      setShowCreatePrincipalUser(false);
      setPrincipalUserPrincipalId("");
      setPrincipalUserName("");
      setPrincipalUserEmail("");
      setPrincipalUserPassword("");
      dispatch(initializeUserManagement());
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <UserManagementHeader activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="max-w-7xl mx-auto px-8">
        {activeTab === "users" && (
          <UsersTab
            users={users ?? []}
            loading={loading}
            error={error}
            onCreatePrincipalUser={openCreatePrincipalUser}
            onAddUser={() => setShowAddUser(true)}
            onUpdateRole={openUpdateRoleModal}
          />
        )}

        {activeTab === "roles" && (
          <RolesTab
            roles={roles}
            selectedRoleId={selectedRoleId}
            uniqueResources={uniqueResources}
            permissions={permissions}
            loading={loading}
            onRoleChange={handleRoleChange}
            onPermissionToggle={handlePermissionToggle}
            onAddRole={() => setShowAddRole(true)}
          />
        )}
      </div>

      <CreatePrincipalUserModal
        show={showCreatePrincipalUser}
        onHide={() => setShowCreatePrincipalUser(false)}
        principalId={principalUserPrincipalId}
        name={principalUserName}
        email={principalUserEmail}
        password={principalUserPassword}
        onPrincipalIdChange={setPrincipalUserPrincipalId}
        onNameChange={setPrincipalUserName}
        onEmailChange={setPrincipalUserEmail}
        onPasswordChange={setPrincipalUserPassword}
        onSubmit={handleCreatePrincipalUserSubmit}
        loading={loading}
        error={error}
        principalList={principalList}
      />

      <AddUserModal
        show={showAddUser}
        onHide={() => setShowAddUser(false)}
        name={newUserName}
        email={newUserEmail}
        password={newUserPassword}
        roleId={newUserRoleId}
        roles={roles}
        onNameChange={setNewUserName}
        onEmailChange={setNewUserEmail}
        onPasswordChange={setNewUserPassword}
        onRoleIdChange={setNewUserRoleId}
        onSubmit={handleAddUserSubmit}
      />

      <AddRoleModal
        show={showAddRole}
        onHide={() => setShowAddRole(false)}
        roleName={newRoleName}
        description={newRoleDescription}
        onRoleNameChange={setNewRoleName}
        onDescriptionChange={setNewRoleDescription}
        onSubmit={handleAddRoleSubmit}
      />

      <UpdateRoleModal
        show={showUpdateRole}
        onHide={() => setShowUpdateRole(false)}
        selectedRoleResourceId={selectedRoleResourceId}
        roles={roles}
        onRoleChange={setSelectedRoleResourceId}
        onSubmit={handleUpdateRoleSubmit}
      />
    </div>
  );
};

export default UserManagementDashboard;
